import React, { useEffect, useState } from 'react';
import {
    Panel,
    TextField,
    IPanelStyles,
    PanelType,
    Dropdown,
    IDropdownOption,
    PrimaryButton,
    mergeStyleSets,
} from '@fluentui/react';
import DateTimePickerComponent from '../date-time-picker/date-time-picker.component';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import moment from 'moment';

import useAuthStatus from '../../hooks/auth/AuthHook';
import ApiService from '../../services/api/api.service';

import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ItemTarea from '../../models/ItemTarea';
import ItemTipoTrabajo from '../../models/ItemTipoTrabajo';

const panelStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {
        marginTop: 0,
    },
    contentInner: {},
    scrollableContent: {},
    navigation: {
        backgroundColor: 'var(--color-primario)',
        padding: 10,
        alignItems: 'center',
    },
    header: {
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 5,
        paddingBottom: 5,
    },
    headerText: {
        color: '#ffffff',
    },
    content: {
        padding: 0,
    },
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: '#ffffff',
                backgroundColor: 'transparent',
                padding: 0,
            },
            rootHovered: {
                backgroundColor: '#000000',
                color: '#ffffff',
                borderColor: '#000000',
            },
        },
    },
};

const formClasses = mergeStyleSets({
    sectionHeader: {
        display: 'block',
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '8px',
        marginTop: '12px',
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 12px',
        gap: 8,
    },
    fieldLabel: {
        minWidth: 60,
        fontSize: 13,
        color: '#333',
        fontWeight: 500,
    },
    fieldValue: {
        flex: 1,
    },
    footer: {
        padding: '12px 12px',
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '1px solid #ececec',
        marginTop: 8,
    },
    saveButton: {
        backgroundColor: 'var(--color-primario)',
        borderColor: 'var(--color-primario)',
        selectors: {
            ':hover': {
                backgroundColor: 'var(--color-primario-dark, #c0504d)',
                borderColor: 'var(--color-primario-dark, #c0504d)',
            },
        },
    },
    textAreaWrapper: {
        padding: '4px 12px 8px',
    },
});

interface IWorkPartCreateComponentProps {
    work: ItemTarea;
    workPart?: ItemRegistroTiempo;
    onClose: () => void;
    onWorkPartCreatedOrEdited?: () => void;
    unfinishedWorkPart?: ItemRegistroTiempo;
};

const WorkPartCreateComponent = (props: IWorkPartCreateComponentProps) => {
    const auth = useAuthStatus();

    const [workTypes, setWorkTypes] = useState<ItemTipoTrabajo[]>([]);
    const [workPart, setWorkPart] = useState<ItemRegistroTiempo>(props.workPart ? props.workPart : new ItemRegistroTiempo());
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<ItemTipoTrabajo[]>("OrdenesTrabajo", "GetListaTiposTrabajos", auth.user).then((response: ItemTipoTrabajo[]) => {
                setWorkTypes(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }

        return () => {
            setWorkTypes([]);
            setLoading(false);
        };
    }, [auth.user]);

    useEffect(() => {
        setWorkPart((workPart: ItemRegistroTiempo) => {
            if (!workPart.Id) {
                workPart.Id = `${auth.user?.IdUsuario}${moment().unix().toString()}`;
                workPart.IdUsuario = auth.user?.IdUsuario || "";
                workPart.CodigoCliente = auth.user?.CodigoCliente || "";
                workPart.IdOrden = props.work.IdOrden;
                workPart.IdTarea = props.work.Id;
                workPart.Inicio = `${moment().format('YYYY-MM-DD HH:mm')}:00`;
                workPart.FechaAltaReal = moment().format('YYYY-MM-DD HH:mm:ss');
                workPart.FechaAlta = moment().format('YYYY-MM-DD HH:mm:ss');
                workPart.FechaUltimaModificacion = moment().format('YYYY-MM-DD HH:mm:ss');
            }
            return workPart;
        });

        return () => {
            setWorkPart(new ItemRegistroTiempo());
        };
    }, [auth.user, props.work]);

    useEffect(() => {
        if (workPart.Inicio && workPart.Fin) {
            let minutes : number = moment(workPart.Fin, 'YYYY-MM-DD HH:mm:ss').diff(moment(workPart.Inicio, 'YYYY-MM-DD HH:mm:ss'), 'minutes');
            setWorkPart((wp: ItemRegistroTiempo) => {
                wp.TiempoEmpleado = minutes / 60;
                return wp;
            });
        }
    }, [workPart.Inicio, workPart.Fin]);

    const onChangeWorkType = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ItemTipoTrabajo>, index?: number) : void => {
        setWorkPart({
            ...workPart,
            TipoTrabajo: option?.data?.Codigo || "",
        });
    };

    const onChangeInit = (value: string) : void => {
        setWorkPart({
            ...workPart,
            Inicio: value,
        });
    };

    const onChangeEnd = (value: string) : void => {
        setWorkPart({
            ...workPart,
            Fin: value,
        });
    };

    const onChangeDescription = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setWorkPart({
            ...workPart,
            Descripcion: newValue ? newValue : "",
        });
    };

    const onFinishUnfinishedWorkPart = () : Promise<void> => {
        return new Promise((resolve, reject) => {
            if (auth.user) {
                if (props.unfinishedWorkPart) {
                    let unfinishedWorkPart : ItemRegistroTiempo = props.unfinishedWorkPart;
                    unfinishedWorkPart.Fin = moment().format("YYYY-MM-DD HH:mm:ss");
                    ApiService.Post<string>("OrdenesTrabajo", "SetRegistroTiempo", auth.user, {
                        "ItemRegistroTiempo": props.unfinishedWorkPart,
                    }).then(() => {
                        resolve()
                    }, (error: string) => {
                        reject(error);
                    });
                }
                else {
                    resolve();
                }
            }
            else {
                reject("Ha ocurrido un error inesperado. Por favor, intente nuevamente.");
            }
        });
    };

    const onSubmit = () => {
        setLoading(true);
        onFinishUnfinishedWorkPart().then(() => {
            if (auth.user) {
                ApiService.Post<string>("OrdenesTrabajo", "SetRegistroTiempo", auth.user, {
                    "ItemRegistroTiempo": workPart,
                }).then(() => {
                    if (props.onWorkPartCreatedOrEdited) {
                        props.onWorkPartCreatedOrEdited();
                    }
                    props.onClose();
                    setLoading(false);
                }, (error: string) => {
                    setError(error);
                    setLoading(false);
                });
            }
        }, (error: string) => {
            setError(error);
            setLoading(false);
        });
    };

    return (
        <Panel
            isOpen={true}
            headerText="Actuación"
            onDismiss={props.onClose}
            styles={panelStyles}
            type={PanelType.medium}>

            <span className={formClasses.sectionHeader}>Tipo de actuación</span>
            <div className={formClasses.fieldRow}>
                <span className={formClasses.fieldLabel}>Tipo</span>
                <div className={formClasses.fieldValue}>
                    <Dropdown
                        options={workTypes.map((wt: ItemTipoTrabajo) => ({
                            key: wt.Codigo,
                            text: wt.Descripcion,
                            data: wt,
                        }))}
                        defaultSelectedKey={workPart.TipoTrabajo}
                        onChange={onChangeWorkType} />
                </div>
            </div>

            <span className={formClasses.sectionHeader}>Tiempo empleado</span>
            <div className={formClasses.fieldRow}>
                <span className={formClasses.fieldLabel}>Inicio</span>
                <div className={formClasses.fieldValue}>
                    <DateTimePickerComponent
                        value={workPart.Inicio}
                        onChange={onChangeInit} />
                </div>
            </div>
            <div className={formClasses.fieldRow}>
                <span className={formClasses.fieldLabel}>Fin</span>
                <div className={formClasses.fieldValue}>
                    <DateTimePickerComponent
                        value={workPart.Fin}
                        onChange={onChangeEnd} />
                </div>
            </div>

            <span className={formClasses.sectionHeader}>Descripción del trabajo</span>
            <div className={formClasses.textAreaWrapper}>
                <TextField
                    defaultValue={workPart.Descripcion}
                    onChange={onChangeDescription}
                    multiline
                    rows={4} />
            </div>

            <ErrorMessageComponent message={error} />

            <div className={formClasses.footer}>
                <PrimaryButton
                    text="Guardar"
                    className={formClasses.saveButton}
                    onClick={onSubmit} />
            </div>

            <LoaderComponent loading={loading} />
        </Panel>
    );
};

export default WorkPartCreateComponent;
